import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Lock, LogOut, Plus, Send, Copy, Shield, RefreshCw, 
  TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownLeft,
  Import, QrCode, History, Sparkles, Bot, Crown
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { QRCodeSVG } from "qrcode.react";
import { API } from "../App";

// Chain config - all supported chains
const CHAIN_CONFIG = {
  xrp: { name: "XRP", color: "#23292F", icon: "🌊" },
  sol: { name: "Solana", color: "#9945FF", icon: "☀️" },
  xlm: { name: "Stellar", color: "#14B8E0", icon: "⭐" },
  hbar: { name: "HBAR", color: "#3A3A3A", icon: "ℏ" },
  btc: { name: "Bitcoin", color: "#F7931A", icon: "₿" },
  eth: { name: "Ethereum", color: "#627EEA", icon: "⟠" },
  link: { name: "Chainlink", color: "#375BD2", icon: "⬡" },
  arb: { name: "Arbitrum", color: "#28A0F0", icon: "🔷" },
  matic: { name: "Polygon", color: "#8247E5", icon: "🟣" },
  avax: { name: "Avalanche", color: "#E84142", icon: "🔺" },
  atom: { name: "Cosmos", color: "#2E3148", icon: "⚛️" },
  near: { name: "NEAR", color: "#00C08B", icon: "🌐" }
};

const CHART_COLORS = ["#FFD700", "#00FFFF", "#9945FF", "#14B8E0", "#F7931A", "#627EEA", "#375BD2", "#28A0F0", "#8247E5", "#E84142", "#2E3148", "#00C08B"];

// Chains requiring memo/destination tag for exchange deposits
const MEMO_CHAINS = {
  xrp:  { label: "Destination Tag", placeholder: "e.g. 12345678 (from exchange)", inputType: "number" },
  xlm:  { label: "Memo",            placeholder: "Memo text or ID (from exchange)", inputType: "text" },
  hbar: { label: "Memo",            placeholder: "Memo (from exchange)",            inputType: "text" },
  atom: { label: "Memo",            placeholder: "Memo (from exchange)",            inputType: "text" },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [wallets, setWallets] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [prices, setPrices] = useState({});
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreateWallet, setShowCreateWallet] = useState(false);
  const [showImportWallet, setShowImportWallet] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [sendForm, setSendForm] = useState({ to_address: "", amount: "", memo: "" });
  const [importForm, setImportForm] = useState({ chain: "", seed_phrase: "", name: "" });
  const [isTestnetMode, setIsTestnetMode] = useState(true); // Default to testnet for real blockchain
  const [creatingWallet, setCreatingWallet] = useState(null); // Track which chain is being created
  const [syncingWallet, setSyncingWallet] = useState(null); // Track which wallet is syncing
  const [newWalletSeed, setNewWalletSeed] = useState(null); // Store seed for display after creation
  const [supportedChains, setSupportedChains] = useState(null); // Chains from backend registry
  const [activatingChain, setActivatingChain] = useState(null); // Track chain being activated

  // Fetch user if not in state
  useEffect(() => {
    const fetchUser = async () => {
      if (!user) {
        try {
          const token = localStorage.getItem('auth_token');
          const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
          const response = await axios.get(`${API}/auth/me`, config);
          setUser(response.data);
        } catch (error) {
          navigate('/login');
        }
      }
    };
    fetchUser();
  }, [user, navigate]);

  // Fetch data
  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const getAuthConfig = () => {
    const token = localStorage.getItem('auth_token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const fetchData = async () => {
    try {
      const config = getAuthConfig();
      const [walletsRes, portfolioRes, pricesRes, chainsRes] = await Promise.all([
        axios.get(`${API}/wallets`, config),
        axios.get(`${API}/portfolio`, config),
        axios.get(`${API}/prices`, config),
        axios.get(`${API}/chains/supported`, config).catch(() => null)
      ]);
      
      setWallets(walletsRes.data.wallets || []);
      setPortfolio(portfolioRes.data);
      setPrices(pricesRes.data.prices || {});
      if (chainsRes?.data?.chains) {
        setSupportedChains(chainsRes.data.chains);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Data refreshed!");
  };

  const handleCreateWallet = async (chain) => {
    try {
      setCreatingWallet(chain);
      const config = getAuthConfig();
      
      if (isTestnetMode && ['xrp', 'sol', 'xlm', 'eth', 'hbar'].includes(chain)) {
        // Create real testnet wallet
        const response = await axios.post(`${API}/wallets/testnet/create?chain=${chain}`, {}, config);
        
        // Show seed phrase if returned
        if (response.data.seed) {
          setNewWalletSeed({
            chain: chain,
            address: response.data.address,
            seed: response.data.seed,
            chainName: CHAIN_CONFIG[chain].name
          });
        }
        
        toast.success(`${CHAIN_CONFIG[chain].name} testnet wallet created!`, {
          description: response.data.address ? `Address: ${response.data.address.slice(0, 12)}...` : 'Wallet ready!'
        });
      } else {
        // Create simulated wallet
        await axios.post(`${API}/wallets`, { chain }, config);
        toast.success(`${CHAIN_CONFIG[chain].name} wallet created!`);
      }
      
      setShowCreateWallet(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create wallet");
    } finally {
      setCreatingWallet(null);
    }
  };

  const handleActivateChain = async (chain) => {
    try {
      setActivatingChain(chain);
      const config = getAuthConfig();
      const response = await axios.post(`${API}/wallets/activate/${chain}`, {}, config);
      
      const chainName = CHAIN_CONFIG[chain]?.name || chain.toUpperCase();
      toast.success(`${chainName} wallet activated!`, {
        description: response.data.address ? `Address: ${response.data.address.slice(0, 16)}...` : 'Wallet ready!'
      });
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate wallet");
    } finally {
      setActivatingChain(null);
    }
  };

  const handleSyncWallet = async (walletId) => {
    try {
      setSyncingWallet(walletId);
      const config = getAuthConfig();
      const response = await axios.post(`${API}/wallets/${walletId}/sync`, {}, config);
      toast.success(`Balance synced: ${response.data.balance}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to sync balance");
    } finally {
      setSyncingWallet(null);
    }
  };

  const handleRequestFaucet = async (walletId, chain) => {
    try {
      toast.info(`Requesting ${CHAIN_CONFIG[chain].name} testnet funds...`);
      const config = getAuthConfig();
      const response = await axios.post(`${API}/wallets/${walletId}/faucet`, {}, config);
      
      if (response.data.success) {
        toast.success(`Received ${response.data.amount} ${CHAIN_CONFIG[chain].name}!`);
        fetchData();
      } else {
        // Show faucet instructions for chains that need manual action
        if (response.data.faucets || response.data.instructions) {
          toast.info(response.data.message || "Use external faucet", {
            description: response.data.faucets?.join(', ') || response.data.instructions?.join('; ')
          });
        } else {
          toast.error(response.data.error || "Faucet request failed");
        }
      }
    } catch (error) {
      toast.error("Faucet request failed");
    }
  };

  const handleImportWallet = async (e) => {
    e.preventDefault();
    if (!importForm.chain || !importForm.seed_phrase) {
      toast.error("Please select a chain and enter your seed phrase");
      return;
    }
    
    try {
      const config = getAuthConfig();
      await axios.post(`${API}/wallets/import`, importForm, config);
      toast.success(`${CHAIN_CONFIG[importForm.chain].name} wallet imported!`);
      setShowImportWallet(false);
      setImportForm({ chain: "", seed_phrase: "", name: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import wallet");
    }
  };

  const handleShowQR = (wallet) => {
    setSelectedWallet(wallet);
    setShowQRDialog(true);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!selectedWallet) return;

    // Validate XRP destination tag (must be uint32)
    if (selectedWallet.chain === "xrp" && sendForm.memo && sendForm.memo.trim()) {
      const tag = parseInt(sendForm.memo, 10);
      if (isNaN(tag) || tag < 0 || tag > 4294967295) {
        toast.error("Destination Tag must be a number between 0 and 4,294,967,295");
        return;
      }
    }

    try {
      const config = getAuthConfig();
      const isTestnet = selectedWallet.is_testnet || selectedWallet.network;
      
      // Use testnet endpoint for real blockchain transactions
      const endpoint = isTestnet ? `${API}/wallets/testnet/send` : `${API}/transactions/send`;
      
      const payload = {
        wallet_id: selectedWallet.wallet_id,
        to_address: sendForm.to_address,
        amount: parseFloat(sendForm.amount)
      };

      // Include memo/destination_tag if provided
      if (sendForm.memo && sendForm.memo.trim()) {
        if (selectedWallet.chain === "xrp") {
          payload.destination_tag = parseInt(sendForm.memo, 10);
        } else {
          payload.memo = sendForm.memo.trim();
        }
      }

      const response = await axios.post(endpoint, payload, config);
      
      if (isTestnet && response.data.tx_hash) {
        toast.success(`Transaction sent! Hash: ${response.data.tx_hash.slice(0, 16)}...`, {
          description: `New balance: ${response.data.new_balance} ${CHAIN_CONFIG[selectedWallet.chain]?.name}`
        });
      } else {
        toast.success("Transaction sent with cryptographic verification!");
      }
      
      setShowSendDialog(false);
      setSendForm({ to_address: "", amount: "", memo: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Transaction failed");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      localStorage.removeItem('auth_token');
      navigate('/login');
    } catch (error) {
      localStorage.removeItem('auth_token');
      navigate('/login');
    }
  };

  const copyAddress = async (address) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(address);
        toast.success("Address copied!");
      } else {
        // Fallback for older browsers or restricted contexts
        fallbackCopyTextToClipboard(address);
      }
    } catch (err) {
      // Fallback if clipboard API is blocked by permissions policy
      fallbackCopyTextToClipboard(address);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        toast.success("Address copied!");
      } else {
        toast.error("Copy failed. Please select and copy manually.");
      }
    } catch (err) {
      toast.error("Copy failed. Please select and copy manually.");
    }
    
    document.body.removeChild(textArea);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020617]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-[#FFD700] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#FFD700] font-medium tracking-wider">LOADING WALLETS...</p>
        </div>
      </div>
    );
  }

  // Calculate correct total from actual wallet holdings × live prices
  // Backend total_value_usd may include tokens without wallets
  const calculatedTotal = wallets.reduce((sum, wallet) => {
    const balance = wallet.balance || 0;
    const price = prices[wallet.chain]?.price_usd || 0;
    return sum + (balance * price);
  }, 0);

  // Prepare chart data from actual wallet holdings (not backend allocation which may be wrong)
  const walletChartData = wallets
    .map((wallet, index) => {
      const balance = wallet.balance || 0;
      const price = prices[wallet.chain]?.price_usd || 0;
      const value = balance * price;
      return {
        name: `${CHAIN_CONFIG[wallet.chain]?.name || wallet.chain} ${wallet.is_testnet || wallet.network ? 'Testnet' : ''}`.trim(),
        value: value || 0.01,
        percentage: calculatedTotal > 0 ? ((value / calculatedTotal) * 100).toFixed(1) : 0,
        color: CHART_COLORS[index % CHART_COLORS.length]
      };
    })
    .filter(item => item.value > 0.01);
  
  // Use wallet-based chart data if available, otherwise fall back to backend allocation
  const chartData = walletChartData.length > 0 ? walletChartData : (portfolio?.allocation?.map((item, index) => ({
    name: item.name,
    value: item.value_usd || 0.01,
    percentage: item.percentage,
    color: CHART_COLORS[index % CHART_COLORS.length]
  })) || []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#020617] via-[#000A1F] to-[#001133]">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-[#020617]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-[#FFD700]" />
              <span className="text-xl font-bold text-white font-['Syne']"><span className="text-[#FFD700]">X</span>avier</span>
            </div>
            
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                className="text-white/60 hover:text-white"
                disabled={refreshing}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              
              <Button
                onClick={() => navigate('/assistant')}
                className="bg-gradient-to-r from-[#FFD700] to-[#FDB931] text-[#002366] rounded-full px-4"
                data-testid="ai-assistant-btn"
              >
                <Bot className="w-5 h-5 mr-2" />
                AI Assistant
              </Button>
              
              <Button
                onClick={() => navigate('/subscription')}
                variant="outline"
                className="border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 rounded-full px-4"
                data-testid="premium-btn"
              >
                <Crown className="w-5 h-5 mr-2" />
                Premium
              </Button>
              
              <div className="flex items-center gap-3">
                <div className="text-right hidden sm:block">
                  <p className="text-white text-sm font-medium">{user?.name}</p>
                  <p className="text-white/40 text-xs">{user?.email}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  className="text-white/60 hover:text-red-400"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Portfolio Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Total Value Card */}
            <Card className="glass-card lg:col-span-2" data-testid="total-value-card">
              <CardContent className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <p className="text-white/50 text-sm uppercase tracking-wider mb-2">Total Portfolio Value</p>
                    <h2 className="text-5xl font-bold text-[#FFD700]" style={{ fontFamily: "'Syne', sans-serif" }}>
                      ${calculatedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </h2>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate('/vault')}
                      className="btn-gold rounded-lg"
                      data-testid="yield-vault-btn"
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Yield Vault
                    </Button>
                    <Button
                      onClick={() => navigate('/nfts')}
                      variant="outline"
                      className="btn-outline-gold rounded-lg"
                      data-testid="view-nfts-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      NFTs
                    </Button>
                    <Button
                      onClick={() => navigate('/transactions')}
                      variant="outline"
                      className="btn-outline-gold rounded-lg"
                      data-testid="view-history-btn"
                    >
                      <History className="w-4 h-4 mr-2" />
                      History
                    </Button>
                  </div>
                </div>
                
                {/* Quick Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {Object.entries(prices).map(([chain, data]) => {
                    // Find wallet for this chain to get balance
                    const chainWallet = wallets.find(w => w.chain === chain);
                    const tokenBalance = chainWallet?.balance || 0;
                    const walletValue = tokenBalance * (data.price_usd || 0);
                    
                    return (
                      <div key={chain} className="p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-testid={`price-tile-${chain}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-white/40 text-xs uppercase">{CHAIN_CONFIG[chain]?.name}</p>
                          <span className="text-lg">{CHAIN_CONFIG[chain]?.icon}</span>
                        </div>
                        <p className="text-white font-semibold">${data.price_usd?.toFixed(4)}</p>
                        <p className={`text-xs flex items-center gap-1 mb-3 ${data.change_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {data.change_24h >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {Math.abs(data.change_24h || 0).toFixed(2)}%
                        </p>
                        {chainWallet ? (
                          <div className="pt-3 border-t border-white/10">
                            <div className="flex items-center justify-between">
                              <span className="text-white/50 text-xs">Holdings</span>
                              <span className="text-white text-sm font-medium">{tokenBalance.toLocaleString(undefined, { maximumFractionDigits: 4 })}</span>
                            </div>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-white/50 text-xs">Value</span>
                              <span className="text-[#FFD700] text-sm font-semibold">${walletValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="pt-3 border-t border-white/10">
                            <p className="text-white/30 text-xs text-center">No wallet</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Allocation Chart */}
            <Card className="glass-card" data-testid="allocation-chart-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-white/70 text-sm uppercase tracking-wider">Asset Allocation</CardTitle>
              </CardHeader>
              <CardContent>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={4}
                        dataKey="value"
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={({ payload }) => {
                          if (payload?.[0]) {
                            const data = payload[0].payload;
                            return (
                              <div className="glass-card p-3 rounded-lg">
                                <p className="text-white font-medium">{data.name}</p>
                                <p className="text-[#FFD700]">{data.percentage}%</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-white/30">
                    No assets yet
                  </div>
                )}
                <div className="flex flex-wrap gap-2 justify-center mt-4">
                  {chartData.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-white/60 text-xs">{item.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>

        {/* Wallet Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
              <TabsList className="bg-white/5 p-1 rounded-xl flex flex-wrap gap-1 h-auto">
                <TabsTrigger value="overview" className="tab-quantum" data-testid="tab-overview">
                  Overview
                </TabsTrigger>
                {wallets.map((wallet) => (
                  <TabsTrigger 
                    key={wallet.wallet_id} 
                    value={wallet.chain}
                    className="tab-quantum"
                    data-testid={`tab-${wallet.chain}`}
                  >
                    {CHAIN_CONFIG[wallet.chain]?.icon} {CHAIN_CONFIG[wallet.chain]?.name}
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="flex gap-2">
                <Dialog open={showImportWallet} onOpenChange={setShowImportWallet}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="btn-outline-gold rounded-lg" data-testid="import-wallet-btn">
                      <Import className="w-4 h-4 mr-2" />
                      Import
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass-card border-[#FFD700]/20">
                    <DialogHeader>
                      <DialogTitle className="text-white font-['Syne']">Import Wallet</DialogTitle>
                      <DialogDescription className="text-white/50">
                        Import an existing wallet using your seed phrase
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleImportWallet} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label className="text-white/70">Select Chain</Label>
                        <Select
                          value={importForm.chain}
                          onValueChange={(value) => setImportForm(prev => ({ ...prev, chain: value }))}
                        >
                          <SelectTrigger className="bg-[#000A1F] border-[#FFD700]/20 text-white" data-testid="import-chain-select">
                            <SelectValue placeholder="Select chain" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0B1221] border-[#FFD700]/20">
                            {Object.entries(CHAIN_CONFIG).map(([key, config]) => (
                              <SelectItem key={key} value={key} className="text-white">
                                {config.icon} {config.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/70">Seed Phrase (12 or 24 words)</Label>
                        <Textarea
                          value={importForm.seed_phrase}
                          onChange={(e) => setImportForm(prev => ({ ...prev, seed_phrase: e.target.value }))}
                          placeholder="Enter your seed phrase separated by spaces..."
                          className="input-quantum min-h-[100px]"
                          data-testid="import-seed-input"
                        />
                        <p className="text-white/30 text-xs">
                          Never share your seed phrase with anyone
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/70">Wallet Name (optional)</Label>
                        <Input
                          value={importForm.name}
                          onChange={(e) => setImportForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="My Imported Wallet"
                          className="input-quantum"
                          data-testid="import-name-input"
                        />
                      </div>
                      <Button type="submit" className="w-full btn-gold rounded-lg" data-testid="import-submit-btn">
                        <Import className="w-4 h-4 mr-2" />
                        Import Wallet
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>

                <Dialog open={showCreateWallet} onOpenChange={setShowCreateWallet}>
                  <DialogTrigger asChild>
                    <Button className="btn-gold rounded-lg" data-testid="create-wallet-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      New Wallet
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass-card border-[#FFD700]/20">
                    <DialogHeader>
                      <DialogTitle className="text-white font-['Syne']">Create New Wallet</DialogTitle>
                      <DialogDescription className="text-white/50">
                        Create a real testnet wallet connected to blockchain
                      </DialogDescription>
                    </DialogHeader>
                    
                    {/* Testnet Mode Indicator */}
                    <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-green-400" />
                        <span className="text-sm text-white">
                          Testnet Mode (Real Blockchain)
                        </span>
                      </div>
                      <span className="text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded">Active</span>
                    </div>
                    
                    <p className="text-xs text-cyan-400/70 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      XRP, SOL, XLM get auto-funded from faucets. ETH/HBAR need manual faucet.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      {Object.entries(CHAIN_CONFIG).map(([chain, config]) => {
                        const exists = wallets.some(w => w.chain === chain);
                        const isCreating = creatingWallet === chain;
                        const isTestnetSupported = ['xrp', 'sol', 'xlm', 'eth', 'hbar'].includes(chain);
                        const isEvm = ['eth', 'arb', 'matic', 'avax', 'link'].includes(chain);
                        
                        return (
                          <Button
                            key={chain}
                            variant="outline"
                            className={`h-24 flex flex-col items-center justify-center gap-2 border-white/10 relative
                              ${exists ? 'opacity-50 cursor-not-allowed' : 'hover:border-[#FFD700]/50 hover:bg-[#FFD700]/5'}
                              ${isCreating ? 'animate-pulse' : ''}`}
                            onClick={() => !exists && !isCreating && handleCreateWallet(chain)}
                            disabled={exists || isCreating}
                            data-testid={`create-${chain}-wallet-btn`}
                          >
                            {isCreating ? (
                              <RefreshCw className="w-6 h-6 text-[#FFD700] animate-spin" />
                            ) : (
                              <span className="text-2xl">{config.icon}</span>
                            )}
                            <span className="text-white">{config.name}</span>
                            {exists && <span className="text-xs text-white/40">Created</span>}
                            {isTestnetMode && isTestnetSupported && !exists && (
                              <span className="absolute top-1 right-1 text-[8px] px-1 py-0.5 rounded bg-green-500/20 text-green-400">TESTNET</span>
                            )}
                          </Button>
                        );
                      })}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {wallets.length === 0 ? (
                  <Card className="glass-card col-span-2 p-12 text-center">
                    <Wallet className="w-16 h-16 text-[#FFD700]/30 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">No Wallets Yet</h3>
                    <p className="text-white/50 mb-6">Create your first secure testnet wallet to get started.</p>
                    <div className="flex gap-4 justify-center">
                      <Button onClick={() => setShowCreateWallet(true)} className="btn-gold rounded-lg">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Wallet
                      </Button>
                    </div>
                  </Card>
                ) : (
                  <>
                    {wallets.map((wallet, index) => (
                      <WalletCard
                        key={wallet.wallet_id}
                        wallet={wallet}
                        price={prices[wallet.chain]}
                        index={index}
                        onSend={() => {
                          setSelectedWallet(wallet);
                          setShowSendDialog(true);
                        }}
                        onCopy={copyAddress}
                        onShowQR={() => handleShowQR(wallet)}
                        onSync={handleSyncWallet}
                        onFaucet={handleRequestFaucet}
                        isSyncing={syncingWallet === wallet.wallet_id}
                      />
                    ))}
                    
                    {/* Show chains the user doesn't have yet (added after registration) */}
                    {(() => {
                      const userChains = new Set(wallets.map(w => w.chain));
                      const allChains = supportedChains ? Object.keys(supportedChains) : Object.keys(CHAIN_CONFIG);
                      const missingChains = allChains.filter(c => !userChains.has(c));
                      
                      if (missingChains.length === 0) return null;
                      
                      return missingChains.map((chain, index) => {
                        const config = CHAIN_CONFIG[chain];
                        if (!config) return null;
                        const isActivating = activatingChain === chain;
                        
                        return (
                          <motion.div
                            key={`activate-${chain}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: (wallets.length + index) * 0.05 }}
                          >
                            <Card className="wallet-card group border-dashed border-[#FFD700]/20" data-testid={`activate-card-${chain}`}>
                              <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-[#FFD700]/5 flex items-center justify-center text-2xl opacity-50">
                                      {config.icon}
                                    </div>
                                    <div>
                                      <h3 className="text-white/60 font-semibold">{config.name}</h3>
                                      <p className="text-white/30 text-sm">Not activated</p>
                                    </div>
                                  </div>
                                  <span className="text-[8px] px-1.5 py-0.5 rounded bg-[#FFD700]/10 text-[#FFD700]/60 uppercase font-medium">
                                    New
                                  </span>
                                </div>
                                
                                <div className="mb-4 p-3 rounded-lg bg-black/20 border border-white/5">
                                  <p className="text-white/30 text-sm text-center">
                                    🆕 {config.name} network now supported
                                  </p>
                                </div>
                                
                                <Button
                                  className="w-full btn-gold rounded-lg"
                                  onClick={() => handleActivateChain(chain)}
                                  disabled={isActivating}
                                  data-testid={`activate-btn-${chain}`}
                                >
                                  {isActivating ? (
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Plus className="w-4 h-4 mr-2" />
                                  )}
                                  {isActivating ? 'Activating...' : `Activate ${config.name}`}
                                </Button>
                              </CardContent>
                            </Card>
                          </motion.div>
                        );
                      });
                    })()}
                  </>
                )}
              </div>
            </TabsContent>

            {/* Individual Wallet Tabs */}
            {wallets.map((wallet) => (
              <TabsContent key={wallet.wallet_id} value={wallet.chain} className="mt-0">
                <WalletDetail
                  wallet={wallet}
                  price={prices[wallet.chain]}
                  onSend={() => {
                    setSelectedWallet(wallet);
                    setShowSendDialog(true);
                  }}
                  onCopy={copyAddress}
                  onShowQR={() => handleShowQR(wallet)}
                />
              </TabsContent>
            ))}
          </Tabs>
        </motion.div>

        {/* QR Code Dialog */}
        <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
          <DialogContent className="glass-card border-[#FFD700]/20 max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white font-['Syne'] text-center">
                Receive {selectedWallet && CHAIN_CONFIG[selectedWallet.chain]?.name}
              </DialogTitle>
              <DialogDescription className="text-white/50 text-center">
                Scan this QR code to send funds to this wallet
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4 py-4">
              {selectedWallet && (
                <>
                  <div className="p-4 bg-white rounded-xl">
                    <QRCodeSVG
                      value={selectedWallet.address}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  <div className="w-full p-3 rounded-lg bg-black/30">
                    <p className="text-white/40 text-xs uppercase mb-1">Wallet Address</p>
                    <code className="text-white/70 text-xs break-all">{selectedWallet.address}</code>
                  </div>
                  <Button
                    onClick={() => copyAddress(selectedWallet.address)}
                    className="w-full btn-outline-gold rounded-lg"
                    variant="outline"
                    data-testid="copy-qr-address-btn"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Address
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Send Dialog */}
        <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
          <DialogContent className="glass-card border-[#FFD700]/20">
            <DialogHeader>
              <DialogTitle className="text-white font-['Syne']">
                Send {selectedWallet && CHAIN_CONFIG[selectedWallet.chain]?.name}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSend} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-white/70">Recipient Address</Label>
                <Input
                  value={sendForm.to_address}
                  onChange={(e) => setSendForm({ ...sendForm, to_address: e.target.value })}
                  placeholder="Enter recipient address"
                  className="input-quantum"
                  required
                  data-testid="send-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Amount</Label>
                <Input
                  type="number"
                  step="0.000001"
                  value={sendForm.amount}
                  onChange={(e) => setSendForm({ ...sendForm, amount: e.target.value })}
                  placeholder="0.00"
                  className="input-quantum"
                  required
                  data-testid="send-amount-input"
                />
                {selectedWallet && (
                  <p className="text-white/40 text-xs">
                    Available: {selectedWallet.balance} {CHAIN_CONFIG[selectedWallet.chain]?.name}
                  </p>
                )}
              </div>
              {/* Memo / Destination Tag — conditional per chain */}
              {selectedWallet && MEMO_CHAINS[selectedWallet.chain] && (
                <div className="space-y-2">
                  <Label className="text-white/70">
                    {MEMO_CHAINS[selectedWallet.chain].label}
                    <span className="text-white/30 font-normal ml-2">(optional)</span>
                  </Label>
                  <Input
                    type={MEMO_CHAINS[selectedWallet.chain].inputType}
                    value={sendForm.memo}
                    onChange={(e) => setSendForm({ ...sendForm, memo: e.target.value })}
                    placeholder={MEMO_CHAINS[selectedWallet.chain].placeholder}
                    className="input-quantum"
                    data-testid="send-memo-input"
                  />
                  <div className="flex items-start gap-2 p-2 rounded-lg bg-[#FFD700]/10 border border-[#FFD700]/30">
                    <span className="text-[#FFD700] text-xs leading-relaxed">
                      ⚠️ Sending to an exchange? The {MEMO_CHAINS[selectedWallet.chain].label.toLowerCase()} is required — without it your funds may be permanently lost.
                    </span>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 p-3 rounded-lg bg-[#00FFFF]/10 border border-[#00FFFF]/30">
                <Shield className="w-4 h-4 text-[#00FFFF]" />
                <span className="text-[#00FFFF] text-sm">Transaction will be cryptographically verified</span>
              </div>
              <Button type="submit" className="w-full btn-gold rounded-lg" data-testid="send-submit-btn">
                <Send className="w-4 h-4 mr-2" />
                Send Transaction
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Seed Phrase Display Dialog */}
        <Dialog open={!!newWalletSeed} onOpenChange={() => setNewWalletSeed(null)}>
          <DialogContent className="glass-card border-[#FFD700]/20 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white font-['Syne'] flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#FFD700]" />
                Backup Your {newWalletSeed?.chainName} Wallet
              </DialogTitle>
              <DialogDescription className="text-red-400 font-medium">
                ⚠️ IMPORTANT: Save this seed phrase securely. It will NOT be shown again!
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="p-3 rounded-lg bg-black/30 border border-white/10">
                <Label className="text-white/60 text-xs uppercase">Address</Label>
                <code className="text-white text-sm block mt-1 break-all">{newWalletSeed?.address}</code>
              </div>
              
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                <Label className="text-red-400 text-xs uppercase flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Secret Seed / Private Key
                </Label>
                <code className="text-white font-mono text-sm block mt-2 break-all select-all p-2 bg-black/40 rounded">
                  {newWalletSeed?.seed}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 border-red-500/30 text-red-400 hover:bg-red-500/10"
                  onClick={() => {
                    fallbackCopyTextToClipboard(newWalletSeed?.seed || '');
                  }}
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copy Seed
                </Button>
              </div>
              
              <div className="text-xs text-white/50 space-y-1">
                <p>• Never share this seed with anyone</p>
                <p>• Store it in a secure, offline location</p>
                <p>• Anyone with this seed can access your funds</p>
              </div>
              
              <Button
                className="w-full btn-gold"
                onClick={() => setNewWalletSeed(null)}
              >
                I've Saved My Seed
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

// Wallet Card Component
const WalletCard = ({ wallet, price, index, onSend, onCopy, onShowQR, onSync, onFaucet, isSyncing }) => {
  const config = CHAIN_CONFIG[wallet.chain];
  const valueUsd = (wallet.balance || 0) * (price?.price_usd || 0);
  const isTestnet = wallet.is_testnet || wallet.network;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card className="wallet-card group" data-testid={`wallet-card-${wallet.chain}`}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#FFD700]/10 flex items-center justify-center text-2xl">
                {config?.icon}
              </div>
              <div>
                <h3 className="text-white font-semibold">{wallet.name}</h3>
                <p className="text-white/40 text-sm">{config?.name}</p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#00FFFF]/10 border border-[#00FFFF]/30">
                <Shield className="w-3 h-3 text-[#00FFFF]" />
                <span className="text-[#00FFFF] text-xs">Secured</span>
              </div>
              {isTestnet && (
                <span className="text-[8px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 uppercase">
                  {wallet.network || 'testnet'}
                </span>
              )}
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between">
              <p className="text-white/40 text-xs uppercase mb-1">Balance</p>
              {isTestnet && onSync && (
                <button
                  onClick={() => onSync(wallet.wallet_id)}
                  className="text-white/40 hover:text-[#00FFFF] transition-colors"
                  disabled={isSyncing}
                  title="Sync from blockchain"
                >
                  <RefreshCw className={`w-3 h-3 ${isSyncing ? 'animate-spin text-[#00FFFF]' : ''}`} />
                </button>
              )}
            </div>
            <p className="text-2xl font-bold text-white">
              {wallet.balance?.toLocaleString() || '0'} <span className="text-white/40 text-sm">{config?.name}</span>
            </p>
            <p className="text-[#FFD700]">${valueUsd.toFixed(2)} USD</p>
          </div>

          <div className="mb-4 p-3 rounded-lg bg-black/30">
            <p className="text-white/40 text-xs uppercase mb-1">Address</p>
            <div className="flex items-center gap-2">
              <code className="text-white/70 text-xs truncate flex-1">{wallet.address || 'Pending registration'}</code>
              {wallet.address && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-white/40 hover:text-[#FFD700]"
                  onClick={() => onCopy(wallet.address)}
                  data-testid={`copy-address-${wallet.chain}`}
                >
                  <Copy className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10"
              onClick={onSend}
              data-testid={`send-btn-${wallet.chain}`}
            >
              <ArrowUpRight className="w-4 h-4 mr-1" />
              Send
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 border-white/20 text-white/70 hover:bg-white/5"
              onClick={onShowQR}
              data-testid={`receive-btn-${wallet.chain}`}
            >
              <QrCode className="w-4 h-4 mr-1" />
              Receive
            </Button>
            {isTestnet && onFaucet && ['xrp', 'sol', 'xlm'].includes(wallet.chain) && (
              <Button
                variant="outline"
                size="sm"
                className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                onClick={() => onFaucet(wallet.wallet_id, wallet.chain)}
                title="Get testnet funds"
              >
                <Sparkles className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Wallet Detail Component
const WalletDetail = ({ wallet, price, onSend, onCopy, onShowQR }) => {
  const config = CHAIN_CONFIG[wallet.chain];
  const valueUsd = (wallet.balance || 0) * (price?.price_usd || 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Info */}
      <Card className="glass-card lg:col-span-2">
        <CardContent className="p-8">
          <div className="flex items-start justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#FFD700]/10 flex items-center justify-center text-4xl">
                {config?.icon}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white font-['Syne']">{wallet.name}</h2>
                <p className="text-white/40">{config?.name} Wallet</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FFFF]/10 border border-[#00FFFF]/30">
              <Shield className="w-5 h-5 text-[#00FFFF]" />
              <span className="text-[#00FFFF] font-medium">Ed25519 Secured</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-8">
            <div>
              <p className="text-white/40 text-sm uppercase mb-2">Balance</p>
              <p className="text-4xl font-bold text-white">{wallet.balance?.toLocaleString() || '0'}</p>
              <p className="text-white/40">{config?.name}</p>
            </div>
            <div>
              <p className="text-white/40 text-sm uppercase mb-2">Value (USD)</p>
              <p className="text-4xl font-bold text-[#FFD700]">${valueUsd.toFixed(2)}</p>
              <p className={`flex items-center gap-1 ${(price?.change_24h || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {(price?.change_24h || 0) >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {Math.abs(price?.change_24h || 0).toFixed(2)}% (24h)
              </p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-black/30 mb-6">
            <p className="text-white/40 text-xs uppercase mb-2">Wallet Address</p>
            <div className="flex items-center gap-3">
              <code className="text-white font-mono flex-1 break-all">{wallet.address}</code>
              <Button
                variant="ghost"
                size="icon"
                className="text-white/40 hover:text-[#FFD700]"
                onClick={() => onCopy(wallet.address)}
              >
                <Copy className="w-5 h-5" />
              </Button>
            </div>
          </div>

          <div className="flex gap-4">
            <Button className="flex-1 btn-gold rounded-lg h-12" onClick={onSend}>
              <Send className="w-5 h-5 mr-2" />
              Send {config?.name}
            </Button>
            <Button 
              variant="outline" 
              className="flex-1 btn-outline-gold rounded-lg h-12"
              onClick={onShowQR}
            >
              <QrCode className="w-5 h-5 mr-2" />
              Receive
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Security Info */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-white/70 text-sm uppercase tracking-wider flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#00FFFF]" />
            Wallet Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-xl bg-[#00FFFF]/5 border border-[#00FFFF]/20">
            <p className="text-white/40 text-xs uppercase mb-1">Wallet Signature</p>
            <code className="text-[#00FFFF] text-xs break-all">
              {wallet.quantum_signature}
            </code>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <span className="text-white/60 text-sm">Encryption</span>
              <span className="text-green-400 text-sm">Ed25519 + SHA3-512</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <span className="text-white/60 text-sm">Key Type</span>
              <span className="text-green-400 text-sm">Ed25519</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <span className="text-white/60 text-sm">Status</span>
              <span className="text-[#00FFFF] text-sm flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-[#00FFFF] animate-pulse" />
                Active
              </span>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-[#FFD700]/5 border border-[#FFD700]/20">
            <p className="text-[#FFD700] text-sm font-medium mb-1">Cryptographic Security</p>
            <p className="text-white/50 text-xs">
              Your wallet is secured with Ed25519 elliptic curve cryptography — the same standard used by Solana, Stellar, and other major blockchains. Post-quantum protection is coming soon.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
